import { useStore } from "@/hooks";
import { useAllPlaylists, useUserPlaylistData } from "./playlistSelectors";
import { useLocation } from "react-router-dom";
import { useIsPartnerApp } from "./featureSelectors";
import { useUserVideoData, useUserVideosWatched } from "./userProfileSelectors";
import {
  useActiveUser,
  useActiveUserPartner,
  useAdvizerMap,
} from "../selectors";
import { AssignmentService } from "@/services/AssignmentService";
import { IAssignment } from "@/types/AssignmentTypes";
import { AssignmentTypes } from "@/components/assignments/assignmentConfigs";

export const useAssignments = () => {
  const assignments = useStore((state) => state.partnerApp?.assignments);
  return assignments;
};

export const useUserAssignments = () => {
  const userAssignments = useStore(
    (state) => state.userAssignments.userAssignments,
  );

  return userAssignments;
};

export const useUserOwnedAssignments = (): IAssignment[] => {
  const userAssignments = useUserAssignments();
  const activeUser = useActiveUser();
  if (!activeUser) return [];
  return Object.values(userAssignments || {}).filter((assignment) => {
    if (assignment.removed) return false;
    return AssignmentService.checkIfUserIsAssignmentOwner({
      assignment,
      userId: activeUser?.id,
    });
  });
};

export const usePartnerUserHasAnyAssignments = () => {
  const assignments = useUserOwnedAssignments();
  return Object.keys(assignments).length > 0;
};

export const useActiveAssignmentId = () => {
  const explicitActiveAssignmentId = useStore(
    (state) => state.partnerApp?.activeAssignmentId,
  );
  return explicitActiveAssignmentId || null;
};

export const useActiveAssignment = () => {
  const fullActiveAssignment = useStore(
    (state) => state.partnerApp?.fullActiveAssignment,
  );

  if (!fullActiveAssignment) return null;

  const totalPlaylistsInAssignment = Object.keys(
    fullActiveAssignment?.playlists || {},
  ).length;

  return {
    ...fullActiveAssignment,
    requiredCount: fullActiveAssignment?.requiredCount || 1,
    totalPlaylistsInAssignment,
  };
};

export const useAssignmentById = (assignmentId: string) => {
  const assignments = useAssignments();
  return assignments[assignmentId] || null;
};

export const useIsAssignmentAdmin = () => {
  const activeUser = useActiveUser();
  return activeUser?.isAssignmentAdmin || false;
};

export const useIsExplicityAssignmentOwner = () => {
  const activeAssignment = useActiveAssignment();
  const activeUser = useActiveUser();
  if (!activeAssignment || !activeUser) return false;
  return activeAssignment?.users?.[activeUser?.id]?.role === "owner";
};

export const useIsAssignmentOwner = () => {
  const isAssignmentAdmin = useIsAssignmentAdmin();
  const isExplicityAssignmentOwner = useIsExplicityAssignmentOwner();
  return isAssignmentAdmin || isExplicityAssignmentOwner;
};

export const useIsActiveAssignmentEmpty = () => {
  const activeAssignment = useActiveAssignment();
  if (activeAssignment?.assignmentType !== AssignmentTypes.specificPlaylists.id)
    return false;
  return Object.keys(activeAssignment?.playlists || {}).length === 0;
};

export const useAssignmentPlaylists = () => {
  const activeAssignment = useActiveAssignment();
  const allPlaylists = useAllPlaylists();
  if (!activeAssignment) return [];
  return AssignmentService.getAssignmentPlaylists({
    assignment: activeAssignment,
    playlists: allPlaylists,
  });
};

export const useIsAssignmentPage = () => {
  const { pathname } = useLocation();
  return pathname.includes("/assignments/");
};

export const useShowAssignmentHeader = () => {
  const isAssignmentPage = useIsAssignmentPage();
  const isPartnerApp = useIsPartnerApp();
  const userIncompleteAssignment = useUserIncompleteAssignment();
  return !isAssignmentPage && !isPartnerApp && userIncompleteAssignment;
};

export const useUserAssignmentResultStatus = () => {
  const activeAssignment = useActiveAssignment();
  const userPlaylistData = useUserPlaylistData();
  const userVideoData = useUserVideoData();
  const advizerMap = useAdvizerMap();
  const userVideosWatched = useUserVideosWatched();
  const currentUserAssignmentResponses = useCurrentUserAssignmentResponses({
    assignmentId: activeAssignment?.id,
  });

  if (!activeAssignment) return null;

  return AssignmentService.getAssignmentResultStatus({
    assignment: activeAssignment,
    userPlaylistData,
    userVideoData,
    advizerMap,
    userVideosWatched,
    userResponses: currentUserAssignmentResponses,
  });
};

export const useUserIncompleteAssignments = () => {
  const userAssignments = useUserAssignments();
  const userPlaylistData = useUserPlaylistData();
  const userVideoData = useUserVideoData();
  const advizerMap = useAdvizerMap();
  const userVideosWatched = useUserVideosWatched();
  const currentUserAssignmentResponses = useCurrentUserAssignmentResponses();
  if (!userAssignments) return [];

  const consentedAssignments = Object.values(userAssignments || {}).filter(
    (assignment) => !!assignment?.consent,
  );

  if (!consentedAssignments.length) return [];

  return consentedAssignments.filter((assignment) => {
    const { status: assignmentResult } =
      AssignmentService.getAssignmentResultStatus({
        assignment,
        userPlaylistData,
        userVideoData,
        advizerMap,
        userVideosWatched: userVideosWatched,
        userResponses: currentUserAssignmentResponses,
      });

    return assignmentResult !== "completed";
  });
};

export const useUserIncompleteAssignment = () => {
  const activeAssignment = useActiveAssignment();
  const userAssignments = useUserAssignments();
  const userPlaylistData = useUserPlaylistData();
  const userVideoData = useUserVideoData();
  const advizerMap = useAdvizerMap();
  const userVideosWatched = useUserVideosWatched();
  const currentUserAssignmentResponses = useCurrentUserAssignmentResponses({
    assignmentId: activeAssignment?.id,
  });
  if (!userAssignments) return null;

  const consentedAssignments = Object.values(userAssignments || {}).filter(
    (assignment) => !!assignment?.consent,
  );

  if (!consentedAssignments.length) return null;

  return (
    consentedAssignments.find((assignment) => {
      const { status: assignmentResult } =
        AssignmentService.getAssignmentResultStatus({
          assignment,
          userPlaylistData,
          userVideoData,
          advizerMap,
          userVideosWatched: userVideosWatched,
          userResponses: currentUserAssignmentResponses,
        });

      return assignmentResult !== "completed";
    }) || null
  );
};

export const useStudentIdsFromAssignment = () => {
  const activeAssignment = useActiveAssignment();
  if (!activeAssignment) return [];
  return AssignmentService.getStudentIdsFromAssignment(activeAssignment);
};

export const useIsAssignmentStudent = () => {
  const activeAssignment = useActiveAssignment();
  const studentIds = useStudentIdsFromAssignment();
  const activeUser = useActiveUser();
  if (!activeAssignment || !activeUser) return false;
  return studentIds.includes(activeUser?.id);
};

export const useUserDidConsentToAssignment = ({
  assignmentId,
}: {
  assignmentId?: string;
}) => {
  const userAssignments = useUserAssignments();
  const activeUser = useActiveUser();
  if (!assignmentId || !activeUser) return false;
  const assignment = userAssignments[assignmentId];

  return !!assignment?.consent;
};

export const useCurrentUserAssignmentResponses = (params?: {
  assignmentId?: string;
}) => {
  const { assignmentId } = params || {};
  const currentUserAssignmentResponses = useStore(
    (state) => state.assignmentResponses.currentUserAssignmentResponses,
  );
  if (!assignmentId) return currentUserAssignmentResponses;
  return currentUserAssignmentResponses?.[assignmentId] || {};
};

export const usePromptResponsesForAssignment = ({
  assignmentId,
  promptId,
}: {
  assignmentId?: string;
  promptId?: string;
}) => {
  const currentUserAssignmentResponses = useCurrentUserAssignmentResponses();
  if (!assignmentId || !promptId) return null;
  return currentUserAssignmentResponses?.[assignmentId]?.[promptId] || null;
};

export const useAssignmentIdForAnalytics = () => {
  const activeAssignment = useActiveAssignment();
  const userIncompleteAssignment = useUserIncompleteAssignment();
  return activeAssignment?.id || userIncompleteAssignment?.id || null;
};

export const useIsAssignmentUploadEnabled = () => {
  const activeUserPartner = useActiveUserPartner();

  return activeUserPartner?.slug == "ucla-anderson";
};

export const useIsStandaloneSeatAccessLocked = () => {
  return useStore(
    (state) => !!state.user.activeUser?.isStandaloneSeatAccessLocked,
  );
};

export const useDidCheckStandaloneSeatAccess = () => {
  return useStore(
    (state) => !!state.user.activeUser?.didCheckStandaloneSeatAccess,
  );
};
